import React, { useState, useEffect, useLayoutEffect, lazy, Suspense, useMemo, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import { Container, Text, Grid, Group, Paper, Title, Box, Autocomplete, ActionIcon, Modal, Stack, Card, Badge, useMantineTheme, useMantineColorScheme, useComputedColorScheme, Loader, Center } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconSearch, IconX, IconLanguage } from '@tabler/icons-react';
import MenuLayout from '../components/MenuLayout';
import { MenuCategory } from '../components/menu/MenuCategory';
import { CartModal } from '../components/menu/CartModal';
import { OnboardingModal } from '../components/menu/OnboardingModal';
import { MenuItemModal } from '../components/menu/MenuItemModal';
import { CategoryNav } from '../components/menu/CategoryNav';
import { useMenu } from '../hooks/useMenu';
import { useActiveUploadSessionOptional, FAST_UPLOAD_NOTIFICATION_ID } from '../context/ActiveUploadSessionContext';
import { useMenuColorScheme } from '../hooks/useMenuColorScheme';
import { useMenuChat } from '../hooks/useMenuChat';
import { MenuItem } from '../types/menu';
import { useMenuAuth } from '../hooks/useMenuAuth';
import { useCart } from '../context/CartContext';
import MenuChatBar from '../components/menu/MenuChatBar';
import PerformanceMonitor from '../components/menu/PerformanceMonitor';
import { detectBrowserLanguage } from '../utils/browserLanguageDetector';
import { getCategoryList } from '../utils/translationHelpers';
import { getMenuItemDisplayText, MenuItemTextField } from '../utils/menuItemDisplayText';
import { t } from '../utils/uiTranslations';
import { setSkipThemePersist } from '../utils/themePersist';
import { trackLanguageChanged, trackMenuItemOpened, trackMenuViewed } from '../utils/analytics';

// Lazy load admin-only components
const AdminEditButton = lazy(() => import('../components/menu/AdminEditButton'));

// Custom hook for managing document title
const useDocumentTitle = (restaurantName?: string) => {
  useEffect(() => {
    if (restaurantName) {
      document.title = `${restaurantName} - Smartmenu`;
    } else {
      document.title = 'Smartmenu';
    }

    return () => {
      document.title = 'Smartmenu';
    };
  }, [restaurantName]);
};

// Custom hook for managing page background
// Uses theme-aware background color based on color scheme
const usePageBackground = () => {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const isDarkMode = colorScheme === 'dark';
  
  // Use useLayoutEffect to set background before paint to prevent flash
  // dark[9] is the darkest color in Mantine's palette (#101113)
  useLayoutEffect(() => {
    document.body.style.backgroundColor = isDarkMode ? theme.colors.dark[9] : theme.white;

    return () => {
      document.body.style.backgroundColor = '';
    };
  }, [theme.white, theme.colors.dark, isDarkMode]);
};

// Custom hook for managing language state
// This hook manages the language display preference for menu items.
// When ?lang=source is added to the URL, it shows the original source language (item.name, item.description).
// By default (or with any other lang parameter), it shows the localized/translated versions.
// On first access, it detects the browser language and sets it as default.
const useLanguageToggle = (menuId?: string) => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Initialize from URL so ?lang=jp is used immediately (chat API gets correct language from first message)
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const lang = searchParams.get('lang');
    if (lang && lang !== 'source') return lang;
    if (lang === 'source') return 'jp';
    return 'en';
  });
  const [hasInitialized, setHasInitialized] = useState(false);

  // Check URL parameter on mount and when searchParams change
  useEffect(() => {
    const langParam = searchParams.get('lang');

    // If there's a URL parameter, use it (user explicitly set language)
    if (langParam && langParam !== 'source') {
      setCurrentLanguage(langParam);
      setHasInitialized(true);
    } else if (langParam === 'source') {
      setCurrentLanguage('jp'); // Default source language
      setHasInitialized(true);
    } else {
      // No URL parameter - check if this is first visit
      if (!hasInitialized) {
        // Check localStorage for saved preference
        const savedLanguage = localStorage.getItem('menuLanguagePreference');

        if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'jp')) {
          // Use saved preference
          setCurrentLanguage(savedLanguage);
        } else {
          // First time visit - detect browser language
          const browserLanguage = detectBrowserLanguage();
          setCurrentLanguage(browserLanguage);

          // Save the detected language preference
          localStorage.setItem('menuLanguagePreference', browserLanguage);
        }
        setHasInitialized(true);
      } else {
        // Not first visit, use default English
        setCurrentLanguage('en');
      }
    }
  }, [searchParams, hasInitialized]);

  // Function to get display text based on language preference
  // Only uses transcreated (friendly) translation, no direct literal translation
  const getDisplayText: (item: MenuItem, field: MenuItemTextField) => string = useCallback(
    (item: MenuItem, field: MenuItemTextField) => getMenuItemDisplayText(item, field, currentLanguage),
    [currentLanguage]
  );

  const setLanguage = useCallback((language: string) => {
    if (language !== currentLanguage) {
      trackLanguageChanged({
        menuId,
        fromLanguage: currentLanguage,
        toLanguage: language
      });
    }

    // Save language preference to localStorage
    localStorage.setItem('menuLanguagePreference', language);

    setSearchParams(prev => {
      if (language === 'en') {
        prev.delete('lang');
      } else {
        prev.set('lang', language);
      }
      return prev;
    });
  }, [setSearchParams, currentLanguage, menuId]);

  return {
    currentLanguage,
    getDisplayText,
    setLanguage
  };
};

// Unified scroll manager - handles both scroll-to-category and scroll spy detection
// This prevents conflicts by having a single system manage all scroll behavior
const useUnifiedScrollManager = (categories: string[], theme: any) => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [isScrollSpyActive, setIsScrollSpyActive] = useState(true);
  const isManualScrollRef = useRef(false);
  const isScrollingRef = useRef(false);
  const scrollableContainerRef = useRef<HTMLElement | null>(null);
  const scrollListenerRef = useRef<(() => void) | null>(null);
  const viewportHeightRef = useRef<number>(window.innerHeight);
  const scrollSpyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Use ref to track current active category to avoid unnecessary state updates
  const activeCategoryRef = useRef<string | null>(activeCategory);
  useEffect(() => {
    activeCategoryRef.current = activeCategory;
  }, [activeCategory]);
  
  // Unified scroll spy detection function
  // Uses ref to check current category without causing re-renders
  const detectActiveCategory = useCallback(() => {
    // Skip if scroll spy is disabled, manual scroll in progress, or smooth scroll in progress
    if (!isScrollSpyActive || isManualScrollRef.current || isScrollingRef.current) return;
    
    const categoryElements = document.querySelectorAll('[data-category]');
    const threshold = viewportHeightRef.current * 0.3; // 30% from top of viewport
    
    let detectedCategory: string | null = null;
    
    categoryElements.forEach((element) => {
      const rect = element.getBoundingClientRect();
      if (rect.top <= threshold && rect.bottom > threshold) {
        detectedCategory = element.getAttribute('data-category');
      }
    });
    
    // Only update state if category actually changed (check ref to avoid unnecessary updates)
    if (detectedCategory && detectedCategory !== activeCategoryRef.current) {
      setActiveCategory(detectedCategory);
    }
  }, [isScrollSpyActive]);
  
  // Update detectActiveCategory ref when it changes (but don't re-initialize listener)
  const detectActiveCategoryRef = useRef(detectActiveCategory);
  useEffect(() => {
    detectActiveCategoryRef.current = detectActiveCategory;
  }, [detectActiveCategory]);
  
  // Set up unified scroll listener - handles both scroll spy and prevents interference
  // This listener is set up ONCE and never re-initialized, preventing conflicts
  useEffect(() => {
    // Find scrollable container once
    if (!scrollableContainerRef.current) {
      const menuLayoutMain = document.getElementById('menu-layout-main');
      scrollableContainerRef.current = menuLayoutMain?.firstElementChild as HTMLElement | null;
    }
    
    const scrollableContainer = scrollableContainerRef.current;
    if (!scrollableContainer) return;
    
    // Create unified scroll listener that handles both detection and interference prevention
    // Uses refs to check state synchronously without re-initializing
    // Debounced to prevent excessive re-renders on scroll
    const listener = () => {
      // If we're in a manual smooth scroll, don't detect (prevents interference)
      if (isScrollingRef.current || isManualScrollRef.current) {
        return;
      }
      
      // Debounce scroll spy detection to reduce re-renders
      // Clear any existing timeout
      if (scrollSpyTimeoutRef.current) {
        clearTimeout(scrollSpyTimeoutRef.current);
      }
      
      // Only detect after scroll has stopped for 100ms
      scrollSpyTimeoutRef.current = setTimeout(() => {
        // Double-check we're still not in a manual scroll
        if (!isScrollingRef.current && !isManualScrollRef.current) {
          // Otherwise, detect active category using the ref (always has latest version)
          // detectActiveCategory already checks isScrollSpyActive internally
          detectActiveCategoryRef.current();
        }
      }, 100);
    };
    
    // Only set up listener once - reuse the same listener forever
    if (!scrollListenerRef.current) {
      scrollableContainer.addEventListener('scroll', listener, { passive: true });
      scrollListenerRef.current = listener;
      
      // Initial detection
      detectActiveCategoryRef.current();
    }
    
    return () => {
      if (scrollListenerRef.current && scrollableContainer) {
        scrollableContainer.removeEventListener('scroll', scrollListenerRef.current);
        scrollListenerRef.current = null;
      }
      // Clean up debounce timeout
      if (scrollSpyTimeoutRef.current) {
        clearTimeout(scrollSpyTimeoutRef.current);
        scrollSpyTimeoutRef.current = null;
      }
    };
  }, []); // Empty deps - set up once, never re-initialize
  
  // Set initial active category if not already set
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0]);
    }
  }, [categories, activeCategory]);

  const handleCategoryClick = useCallback((category: string) => {
    // Temporarily disable scroll spy to prevent conflicts
    setIsScrollSpyActive(false);
    isManualScrollRef.current = true; // Update ref synchronously for scroll spy checks

    // Update active category immediately so nav highlight responds without delay
    setActiveCategory(category);

    // Use a small delay to ensure DOM is ready
    setTimeout(() => {
      const element = document.getElementById(`category-${category}`);

      // Re-acquire the scroll container if the ref is null (can happen on mobile if DOM wasn't
      // ready during mount effect)
      if (!scrollableContainerRef.current) {
        const menuLayoutMain = document.getElementById('menu-layout-main');
        scrollableContainerRef.current = menuLayoutMain?.firstElementChild as HTMLElement | null;
      }
      const scrollableContainer = scrollableContainerRef.current;

      if (element && scrollableContainer) {
        // Use theme scroll offset to account for sticky headers
        const headerOffset = theme.other?.layout?.scrollOffset ?? 100;
        
        // Calculate element position using a reliable method
        // Get the element's position relative to the document, then subtract container's position
        const elementTop = element.offsetTop;
        let parent = element.offsetParent as HTMLElement | null;
        let totalOffset = elementTop;
        
        // Walk up to find the container, summing offsets
        while (parent && parent !== scrollableContainer) {
          totalOffset += parent.offsetTop;
          parent = parent.offsetParent as HTMLElement | null;
        }
        
        // If we didn't find the container in the offsetParent chain, use getBoundingClientRect
        if (parent !== scrollableContainer) {
          const containerRect = scrollableContainer.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();
          totalOffset = scrollableContainer.scrollTop + (elementRect.top - containerRect.top);
        }
        
        // Target scroll position: element position minus header offset
        const targetScrollTop = Math.max(0, totalOffset - headerOffset);
        
        // Set scroll lock to prevent interference during smooth scroll
        isScrollingRef.current = true;
        isManualScrollRef.current = true;
        
        // Scroll the container smoothly to the target position
        scrollableContainer.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth'
        });
        
        // Clear scroll lock after animation completes
        setTimeout(() => {
          isScrollingRef.current = false;
          isManualScrollRef.current = false;
          setIsScrollSpyActive(true);
        }, 1500);
      } else if (element) {
        // Last-resort fallback: scroll the window if no container was found
        isScrollingRef.current = true;
        isManualScrollRef.current = true;

        const headerOffset = theme.other?.layout?.scrollOffset ?? 100;
        const elementRect = element.getBoundingClientRect();
        const targetY = window.pageYOffset + elementRect.top - headerOffset;
        window.scrollTo({ top: targetY, behavior: 'smooth' });

        setTimeout(() => {
          isScrollingRef.current = false;
          isManualScrollRef.current = false;
          setIsScrollSpyActive(true);
        }, 1500);
      } else {
        console.warn('Menu - Category element not found:', category);
      }
    }, 50);

  }, [activeCategory, theme]);

  // Scroll spy update handler - now just updates category (detection happens in unified listener)
  const handleScrollSpyUpdate = useCallback((category: string) => {
    // Only update if not in manual scroll mode
    if (!isManualScrollRef.current && !isScrollingRef.current && category !== activeCategory) {
      setActiveCategory(category);
    }
  }, [activeCategory]);
  
  return {
    activeCategory,
    handleCategoryClick,
    handleScrollSpyUpdate,
    isScrollSpyActive
  };
};

// Utility function to group menu items by category
const groupItemsByCategory = (
  menuPages: any[],
  language: string = 'en'
): Record<string, MenuItem[]> => {
  const grouped = menuPages.reduce((acc, page) => {
    if (page.menuItems && Array.isArray(page.menuItems)) {
      page.menuItems.forEach((item: MenuItem) => {
        const categories = getCategoryList(item, language);
        const categoriesForGrouping = categories.length > 0 ? categories : ['Uncategorized'];

        categoriesForGrouping.forEach((category) => {
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(item);
        });
      });
    }
    return acc;
  }, {} as Record<string, MenuItem[]>);

  // Sort items within each category alphabetically by English localization name
  Object.keys(grouped).forEach(category => {
    grouped[category].sort((a: MenuItem, b: MenuItem) => {
      const nameA = a.props?.translations?.en?.name?.text ?? a.props?.enLocalization?.name ?? a.name ?? '';
      const nameB = b.props?.translations?.en?.name?.text ?? b.props?.enLocalization?.name ?? b.name ?? '';
      return nameA.localeCompare(nameB);
    });
  });

  return grouped;
};

// Utility function to get ordered categories
const getOrderedCategories = (
  categories: string[],
  categoryOrder?: [string, number][]
): string[] => {
  // If no categoryOrder is defined, return categories in original order
  if (!categoryOrder || !Array.isArray(categoryOrder)) {
    return categories;
  }

  // Create a map of category name to order index
  const categoryOrderMap = new Map();
  categoryOrder.forEach(([categoryName, index]) => {
    categoryOrderMap.set(categoryName, index);
  });

  // Sort categories by their order index, with default index of 100 for unknown categories
  return categories.sort((a, b) => {
    const indexA = categoryOrderMap.get(a) ?? 100;
    const indexB = categoryOrderMap.get(b) ?? 100;
    return indexA - indexB;
  });
};

// Component for QR code display - memoized to prevent re-renders
// Uses theme layout constants for QR code size
const QRCodeSection: React.FC<{ qrcode?: string; theme: any; currentLanguage: string }> = React.memo(({ qrcode, theme, currentLanguage }) => {
  if (!qrcode) return null;

  const qrCodeSize = theme.other?.layout?.qrCodeSize ?? 200;

  return (
    <Paper withBorder p="md" mt="xl" mx="md">
      <Title order={3} mb="md">{t('scanToViewMenu', currentLanguage)}</Title>
      <Group justify="center">
        <img
          src={`data:image/png;base64,${typeof qrcode === 'string' ? qrcode : ''}`}
          alt={t('menuQrCodeAlt', currentLanguage)}
          style={{ maxWidth: `${qrCodeSize}px`, height: 'auto' }}
        />
      </Group>
      <Text size="sm" c="dimmed" ta="center" mt="sm">
        {t('qrShareHelp', currentLanguage)}
      </Text>
    </Paper>
  );
});

// Component for menu items display - memoized to prevent re-renders
const MenuItemsSection: React.FC<{
  categories: string[];
  itemsByCategory: Record<string, MenuItem[]>;
  getDisplayText: (item: MenuItem, field: 'name' | 'description' | 'disclaimer') => string;
  onItemSelect: (itemId: string) => void;
  currentLanguage: string;
}> = React.memo(({ categories, itemsByCategory, getDisplayText, onItemSelect, currentLanguage }) => {
  if (Object.keys(itemsByCategory).length === 0) {
    return <Text c="dimmed" mb="xl" px="md">{t('noMenuItems', currentLanguage)}</Text>;
  }

  return (
    <Grid mb="xl" px="md">
      {categories.map((category) => (
        <Grid.Col span={12} key={category}>
          <MenuCategory
            category={category}
            items={itemsByCategory[category]}
            getDisplayText={getDisplayText}
            onItemSelect={onItemSelect}
            currentLanguage={currentLanguage}
          />
        </Grid.Col>
      ))}
    </Grid>
  );
});

// Component for menu hero image - memoized to prevent re-renders
// This displays the main menu image (from menu.imageUrl) above the restaurant name.
// Uses theme layout constants for max height and theme colors for background
const MenuHeroImage: React.FC<{
  imageUrl?: string;
  theme: any;
  currentLanguage: string;
  isDarkMode: boolean;
  onLanguagePickerOpen: () => void;
}> = React.memo(({ imageUrl, theme, currentLanguage, isDarkMode, onLanguagePickerOpen }) => {
  if (!imageUrl) return null;

  const maxHeight = theme.other?.layout?.heroImageMaxHeight ?? 260;
  const aspectRatio = theme.other?.layout?.heroImageAspectRatio ?? '16 / 9';
  const backgroundColor = theme.colors?.gray?.[1] ?? theme.colors?.gray?.[0] ?? '#f5f5f5';

  return (
    <Box
      style={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        aspectRatio,
        maxHeight: maxHeight,
        backgroundColor: backgroundColor,
      }}
    >
      {/* The menu hero image is shown full-width as a category header */}
      <img
        src={imageUrl}
        alt={t('menuHeaderAlt', currentLanguage)}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />

      {/* Floating language picker trigger anchored to hero image */}
      <ActionIcon
        variant="transparent"
        size="lg"
        radius="xl"
        onClick={onLanguagePickerOpen}
        aria-label={t('selectLanguageTitle', currentLanguage)}
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          // Keep a 50% translucent white surface for readability over photos
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(4px)',
          border: isDarkMode ? `1px solid ${theme.colors.dark[3]}` : `1px solid ${theme.colors.gray[3]}`,
          color: isDarkMode ? theme.colors.dark[9] : theme.colors.gray[8],
          boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.45)' : '0 2px 8px rgba(0, 0, 0, 0.18)',
        }}
      >
        <IconLanguage size={20} />
      </ActionIcon>
    </Box>
  );
});

// Bottom spacer component - memoized to prevent re-renders
// Uses theme layout constant for height to ensure content visibility above fixed elements
const BottomSpacer: React.FC<{ theme: any }> = React.memo(({ theme }) => {
  const height = theme.other?.layout?.bottomSpacerHeight ?? 220;
  
  return (
    <div style={{
      height: `${height}px`,
      width: '100%'
    }} />
  );
});

const Menu: React.FC = () => {
  const { menuId } = useParams<{ menuId: string }>();
  const { isAdmin } = useMenuAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const theme = useMantineTheme();

  const uploadSessionParam = searchParams.get('uploadSession');
  const activeUploadSession = useActiveUploadSessionOptional();
  const isLiveFastUpload =
    !!uploadSessionParam &&
    !!activeUploadSession?.clientId &&
    activeUploadSession.clientId === uploadSessionParam &&
    activeUploadSession.isSessionActive;

  const { menu, loading, error } = useMenu(menuId!, {
    refetchIntervalMs: isLiveFastUpload ? 2000 : null,
    refetchNonce: activeUploadSession?.batchVersion ?? 0
  });

  // Remove orphan ?uploadSession= (e.g. page refresh lost React context)
  useEffect(() => {
    if (!uploadSessionParam) return;
    if (activeUploadSession?.clientId === uploadSessionParam) return;
    if (activeUploadSession?.isSessionActive) return;
    if (activeUploadSession?.uploadResponse) return;
    const next = new URLSearchParams(searchParams);
    next.delete('uploadSession');
    setSearchParams(next, { replace: true });
  }, [
    uploadSessionParam,
    activeUploadSession?.clientId,
    activeUploadSession?.isSessionActive,
    activeUploadSession?.uploadResponse,
    searchParams,
    setSearchParams
  ]);

  // Persistent notification while fast upload pipeline finishes in the background
  useEffect(() => {
    if (!isLiveFastUpload || !activeUploadSession) return;
    notifications.show({
      id: FAST_UPLOAD_NOTIFICATION_ID,
      title: 'Finishing your menu',
      message: activeUploadSession.uploadStatus || 'Translating items…',
      loading: true,
      autoClose: false,
      withCloseButton: false
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- same notification id; avoid full object dep (re-renders on every SSE tick)
  }, [isLiveFastUpload, activeUploadSession?.clientId]);

  useEffect(() => {
    if (!isLiveFastUpload || !activeUploadSession) return;
    notifications.update({
      id: FAST_UPLOAD_NOTIFICATION_ID,
      message: activeUploadSession.uploadStatus || 'Translating items…',
      loading: true
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLiveFastUpload,
    activeUploadSession?.uploadStatus,
    activeUploadSession?.uploadProgress,
    activeUploadSession?.detailedStatus,
    activeUploadSession?.currentStage
  ]);

  useEffect(() => {
    if (!activeUploadSession) return;
    if (activeUploadSession.isSessionActive) return;

    const stripUploadSession = () => {
      if (!searchParams.get('uploadSession')) return;
      const next = new URLSearchParams(searchParams);
      next.delete('uploadSession');
      setSearchParams(next, { replace: true });
    };

    if (
      activeUploadSession.uploadResponse &&
      activeUploadSession.lastCompletedMenuId === menuId
    ) {
      notifications.update({
        id: FAST_UPLOAD_NOTIFICATION_ID,
        title: 'Menu ready',
        message: 'All set — translations are saved.',
        color: 'green',
        loading: false,
        autoClose: 5000
      });
      stripUploadSession();
      return;
    }
    if (activeUploadSession.uploadStatus === 'Upload failed') {
      notifications.update({
        id: FAST_UPLOAD_NOTIFICATION_ID,
        title: 'Processing failed',
        message: 'Something went wrong finishing your menu.',
        color: 'red',
        loading: false,
        autoClose: 8000
      });
      stripUploadSession();
    }
  }, [
    menuId,
    activeUploadSession?.isSessionActive,
    activeUploadSession?.uploadResponse,
    activeUploadSession?.uploadStatus,
    activeUploadSession?.lastCompletedMenuId,
    searchParams,
    setSearchParams,
    activeUploadSession
  ]);
  
  // Fetch color scheme early (before full menu loads) to prevent white flash
  // This hook makes a lightweight API call to get just the color scheme
  const { colorScheme: menuColorScheme } = useMenuColorScheme(menuId);
  
  // Get current color scheme for theme-aware styling
  const colorScheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const isDarkMode = colorScheme === 'dark';
  
  // Apply restaurant color scheme IMMEDIATELY when color scheme data is available
  // useLayoutEffect runs synchronously before browser paint to prevent flashing
  const { setColorScheme } = useMantineColorScheme();
  
  // Set color scheme synchronously BEFORE browser paint to prevent flash
  // Use a ref to track the current menuId and color scheme to prevent unnecessary updates
  const colorSchemeSetRef = useRef<string | null>(null);
  const menuIdRef = useRef<string | undefined>(menuId);
  
  // Track menuId changes - only reset color scheme when menuId actually changes
  useEffect(() => {
    const previousMenuId = menuIdRef.current;
    menuIdRef.current = menuId;
    
    // If menuId changed, we're on a different menu - reset the ref
    if (previousMenuId !== menuId) {
      colorSchemeSetRef.current = null;
    }
  }, [menuId]);
  
  // Set color scheme immediately when we get it from the lightweight endpoint
  // This happens BEFORE the full menu loads, preventing white flash
  useLayoutEffect(() => {
    // Only set color scheme if we're still on a menu route
    // This prevents setting dark mode when navigating away to admin
    if (!location.pathname.startsWith('/menus/')) {
      return; // Don't set color scheme if we're not on a menu route
    }
    
    // Use the color scheme from the lightweight endpoint
    // This is available much faster than waiting for the full menu to load
    const targetColorScheme = menuColorScheme;
    
    // Only update if the color scheme has actually changed
    // This prevents unnecessary updates that cause flashing
    if (colorSchemeSetRef.current !== targetColorScheme) {
      // Don't persist menu theme so other tabs don't flicker and next load stays correct
      setSkipThemePersist(true);
      // Set immediately to prevent flash - useLayoutEffect runs before paint
      setColorScheme(targetColorScheme);
      // Also set on document element for immediate CSS application
      document.documentElement.setAttribute('data-mantine-color-scheme', targetColorScheme);
      setSkipThemePersist(false);

      // Update ref to track what we've set
      colorSchemeSetRef.current = targetColorScheme;
    }
  }, [menuColorScheme, location.pathname, setColorScheme]);
  
  // Note: We don't reset color scheme on unmount here because ColorSchemeManager
  // in App.tsx handles route-based color scheme changes immediately when routes change.
  // This prevents flashing during navigation.
  
  // Memoize menu object to prevent MenuChatBar re-renders on scroll
  // Only recreate if menu ID or data actually changes
  const memoizedMenu = useMemo(() => menu, [menu?.id, menu?.menuPages?.length]);
  const [cartOpened, setCartOpened] = useState(false);
  const [onboardingOpened, setOnboardingOpened] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [languageModalOpened, setLanguageModalOpened] = useState(false);
  // Ref to store the search term before autocomplete selection
  // This allows us to restore it after Mantine updates the input value
  const searchTermBeforeSelection = useRef<string>('');
  /** Ensures onboarding auto-opens only once per menu on first paint, not on every poll/refetch (e.g. fast upload live updates). */
  const onboardingAutoOpenDoneForMenuIdRef = useRef<string | null>(null);

  const { currentLanguage, getDisplayText, setLanguage } = useLanguageToggle(menuId);
  const {
    messages,
    newMessage,
    setNewMessage,
    isSending,
    handleSendMessage,
    sendDirectMessage,
    sendInitialMessage
  } = useMenuChat(menu, currentLanguage);

  const { getTotalItems, setMenuId } = useCart();

  // Cart bar and add-to-order visibility: respect restaurant setting (default on for backward compatibility)
  const cartEnabled = menu?.restaurant?.cartEnabled !== false;
  // "Scan to View Menu" section at bottom: respect restaurant setting (default on)
  const showScanSection = menu?.restaurant?.showScanSection !== false;

  // Custom hooks
  useDocumentTitle(menu?.restaurant?.name);
  usePageBackground();

  // Memoize expensive calculations to prevent re-computation on every render
  const itemsByCategory = useMemo(
    () => groupItemsByCategory(menu?.menuPages || [], currentLanguage),
    [menu?.menuPages, currentLanguage]
  );

  const categories = useMemo(() =>
    getOrderedCategories(
      Object.keys(itemsByCategory),
      menu?.categoryOrder
    ),
    [itemsByCategory, menu?.categoryOrder]
  );

  // Food / Drinks section
  const drinkSectionEnabled = menu?.drinkSection?.enabled === true;
  const drinkCategorySet = useMemo(
    () => new Set(menu?.drinkSection?.drinkCategories ?? []),
    [menu?.drinkSection?.drinkCategories]
  );

  // All categories ordered: food first, then drinks
  const orderedCategories = useMemo(() => {
    if (!drinkSectionEnabled) return categories;
    const foodCats = categories.filter(c => !drinkCategorySet.has(c));
    const drinkCats = categories.filter(c => drinkCategorySet.has(c));
    return [...foodCats, ...drinkCats];
  }, [drinkSectionEnabled, categories, drinkCategorySet]);

  // Filter menu items by search term (matches display name)
  // This filters items within each category and removes categories with no matching items
  const filteredItemsByCategory = useMemo(() => {
    if (!searchTerm.trim()) {
      return itemsByCategory;
    }

    const filtered: Record<string, MenuItem[]> = {};
    const searchLower = searchTerm.toLowerCase().trim();

    Object.keys(itemsByCategory).forEach(category => {
      const matchingItems = itemsByCategory[category].filter(item => {
        const displayName = getDisplayText(item, 'name').toLowerCase();
        return displayName.includes(searchLower);
      });

      if (matchingItems.length > 0) {
        filtered[category] = matchingItems;
      }
    });

    return filtered;
  }, [itemsByCategory, searchTerm, getDisplayText]);

  // Get filtered categories (only those with items after filtering), food first then drinks
  const filteredCategories = useMemo(() => {
    const ordered = getOrderedCategories(
      Object.keys(filteredItemsByCategory),
      menu?.categoryOrder
    );
    if (!drinkSectionEnabled) return ordered;
    const foodCats = ordered.filter(c => !drinkCategorySet.has(c));
    const drinkCats = ordered.filter(c => drinkCategorySet.has(c));
    return [...foodCats, ...drinkCats];
  }, [filteredItemsByCategory, menu?.categoryOrder, drinkSectionEnabled, drinkCategorySet]);

  const { activeCategory, handleCategoryClick, handleScrollSpyUpdate, isScrollSpyActive } = useUnifiedScrollManager(filteredCategories, theme);

  // Derive active section from scroll position — mirrors how the category highlight works
  const activeSection = useMemo<'food' | 'drinks'>(() => {
    if (!drinkSectionEnabled || !activeCategory) return 'food';
    return drinkCategorySet.has(activeCategory) ? 'drinks' : 'food';
  }, [drinkSectionEnabled, activeCategory, drinkCategorySet]);

  // Clicking the toggle always jumps to the first category of that section,
  // even if already active (bypasses any no-op guard in handleCategoryClick)
  const handleSectionChange = useCallback((section: 'food' | 'drinks') => {
    const firstCat = filteredCategories.find(c =>
      section === 'drinks' ? drinkCategorySet.has(c) : !drinkCategorySet.has(c)
    );
    if (!firstCat) return;
    handleCategoryClick(firstCat);
  }, [filteredCategories, drinkCategorySet, handleCategoryClick]);

  // Set the menu ID in cart context for analytics tracking
  useEffect(() => {
    if (menuId) {
      setMenuId(menuId);
    }
  }, [menuId, setMenuId]);

  // Reset onboarding auto-open when navigating to a different menu route
  useEffect(() => {
    onboardingAutoOpenDoneForMenuIdRef.current = null;
  }, [menuId]);

  // Open onboarding once when this menu first becomes available (not on each refetch / live poll)
  useEffect(() => {
    if (!menu || loading) return;
    if (menu.id !== menuId) return;
    if (onboardingAutoOpenDoneForMenuIdRef.current === menu.id) return;
    onboardingAutoOpenDoneForMenuIdRef.current = menu.id;
    setOnboardingOpened(true);
  }, [menu, loading, menuId]);

  // Track menu page views once per menu load.
  const trackedMenuViewRef = useRef<string | null>(null);
  useEffect(() => {
    if (!menu || loading) return;
    if (trackedMenuViewRef.current === menu.id) return;

    trackMenuViewed({
      menuId: menu.id,
      restaurantName: menu.restaurant?.name,
      language: currentLanguage
    });
    trackedMenuViewRef.current = menu.id;
  }, [menu, loading, currentLanguage]);

  // Send initial message when chat is first opened
  useEffect(() => {
    if (menu && !loading) {
      sendInitialMessage();
    }
  }, [sendInitialMessage, menu, loading]);

  // Handle URL parameter for selected menu item
  useEffect(() => {
    const itemId = searchParams.get('item');
    setSelectedItemId(itemId);
  }, [searchParams]);

  // Track menu item modal opens (includes deep links with ?item=).
  const trackedItemOpenRef = useRef<string | null>(null);
  useEffect(() => {
    if (!menu?.id || !selectedItemId) return;
    if (trackedItemOpenRef.current === selectedItemId) return;

    trackMenuItemOpened({
      menuId: menu.id,
      menuItemId: selectedItemId,
      language: currentLanguage
    });
    trackedItemOpenRef.current = selectedItemId;
  }, [menu?.id, selectedItemId, currentLanguage]);

  // Flatten all menu items for CategoryNav counting - memoized
  const allMenuItems = useMemo(() => {
    return menu?.menuPages?.flatMap(page => page.menuItems) || [];
  }, [menu?.menuPages]);

  // Generate autocomplete suggestions from all menu item display names
  // This creates a unique list of menu item names for the autocomplete dropdown
  const autocompleteSuggestions = useMemo(() => {
    if (!menu?.menuPages) return [];
    
    // Get all unique display names from menu items
    const displayNames = new Set<string>();
    menu.menuPages.forEach(page => {
      page.menuItems?.forEach((item: MenuItem) => {
        const displayName = getDisplayText(item, 'name');
        if (displayName && displayName.trim()) {
          displayNames.add(displayName);
        }
      });
    });
    
    // Convert Set to sorted array for consistent ordering
    return Array.from(displayNames).sort();
  }, [menu?.menuPages, getDisplayText]);

  // Create a map from display name to menu item ID
  // This allows us to find the menu item when user selects from autocomplete
  const displayNameToItemIdMap = useMemo(() => {
    if (!menu?.menuPages) return new Map<string, string>();
    
    const map = new Map<string, string>();
    menu.menuPages.forEach(page => {
      page.menuItems?.forEach((item: MenuItem) => {
        const displayName = getDisplayText(item, 'name');
        if (displayName && displayName.trim() && !map.has(displayName)) {
          // Use first item found if there are duplicates with same display name
          map.set(displayName, item.id);
        }
      });
    });
    
    return map;
  }, [menu?.menuPages, getDisplayText]);

  // Memoize menu items map for O(1) lookup instead of searching on every call
  const menuItemsByIdMap = useMemo(() => {
    const map = new Map<string, MenuItem>();
    menu?.menuPages?.forEach(page => {
      page.menuItems?.forEach(item => {
        map.set(item.id, item);
      });
    });
    return map;
  }, [menu?.menuPages]);

  // Memoize the scrollToMenuItem function to prevent recreation on every render
  // Uses memoized map for faster lookup - no dependency on menu object
  const scrollToMenuItem = useCallback((itemId: string) => {
    const menuItem = menuItemsByIdMap.get(itemId);

    if (menuItem) {
      const category = getCategoryList(menuItem, currentLanguage)[0] || 'Uncategorized';
      // First scroll to the category
      handleCategoryClick(category);

      // Then find and scroll to the specific menu item
      const itemElement = document.getElementById(`menu-item-${itemId}`);
      if (itemElement) {
        // Use theme scroll offset to account for sticky headers
        const headerOffset = theme.other?.layout?.scrollOffset ?? 100;
        const elementPosition = itemElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  }, [menuItemsByIdMap, handleCategoryClick, currentLanguage, theme]);

  // Memoize cart button click handler
  const handleCartButtonClick = useCallback(() => {
    setCartOpened(true);
  }, []);

  // Memoize modal close handlers
  const handleCartModalClose = useCallback(() => {
    setCartOpened(false);
  }, []);

  const handleOnboardingModalClose = useCallback(() => {
    setOnboardingOpened(false);
  }, []);

  // Handler for selecting a menu item (updates URL)
  const handleMenuItemSelect = useCallback((itemId: string) => {
    setSearchParams(prev => {
      prev.set('item', itemId);
      return prev;
    });
  }, [setSearchParams]);

  // Ref to track the selected autocomplete value (to detect when Mantine updates the input)
  const selectedAutocompleteValue = useRef<string | null>(null);

  // Handle autocomplete option selection - opens the menu item modal
  // Note: Preserves the original search term (doesn't replace it with selected value)
  const handleAutocompleteSelect = useCallback((value: string) => {
    // Store the current search term and selected value before Mantine updates it
    searchTermBeforeSelection.current = searchTerm;
    selectedAutocompleteValue.current = value;
    
    // Find the menu item ID for the selected display name
    const itemId = displayNameToItemIdMap.get(value);
    if (itemId) {
      // Open the menu item modal by calling the handler
      handleMenuItemSelect(itemId);
    }
  }, [displayNameToItemIdMap, handleMenuItemSelect, searchTerm]);

  // Effect to restore search term when it changes due to autocomplete selection
  useEffect(() => {
    // If we have a stored original value and the current search term matches the selected autocomplete value
    // This means Mantine updated the input - restore the original value
    if (
      searchTermBeforeSelection.current !== '' &&
      selectedAutocompleteValue.current !== null &&
      searchTerm === selectedAutocompleteValue.current &&
      searchTerm !== searchTermBeforeSelection.current
    ) {
      // Restore the original search term
      const originalValue = searchTermBeforeSelection.current;
      searchTermBeforeSelection.current = '';
      selectedAutocompleteValue.current = null;
      setSearchTerm(originalValue);
    }
  }, [searchTerm]);

  // Handle search term changes - normal typing updates
  const handleSearchTermChange = useCallback((newValue: string) => {
    // Clear stored values when user types (not selecting from autocomplete)
    if (selectedAutocompleteValue.current === null) {
      searchTermBeforeSelection.current = '';
    }
    setSearchTerm(newValue);
  }, []);

  // Handler for closing menu item modal (clears URL parameter)
  const handleMenuItemModalClose = useCallback(() => {
    trackedItemOpenRef.current = null;
    setSearchParams(prev => {
      prev.delete('item');
      return prev;
    });
  }, [setSearchParams]);

  // Find the selected menu item from all menu pages
  const selectedMenuItem = useMemo(() => {
    if (!selectedItemId || !menu?.menuPages) return null;

    for (const page of menu.menuPages) {
      const item = page.menuItems?.find((item: MenuItem) => item.id === selectedItemId);
      if (item) return item;
    }
    return null;
  }, [selectedItemId, menu?.menuPages]);

  // Loading and error states
  if (loading) return <MenuLayout><Center style={{ minHeight: '60vh' }}><Stack align="center" gap="sm"><Loader size="lg" /><Text c="dimmed" size="sm">{t('loading', currentLanguage)}</Text></Stack></Center></MenuLayout>;
  if (error) return <MenuLayout><Container><Text c="red">{error}</Text></Container></MenuLayout>;
  if (!menu) return <MenuLayout><Container><Text>{t('menuNotFound', currentLanguage)}</Text></Container></MenuLayout>;

  return (
    <MenuLayout>
      <PerformanceMonitor componentName="Menu" props={{ newMessage, isSending, messages: messages.length }} />
      <Container size="100%" px={0} style={{ maxWidth: '100%' }}>
        {/* Admin Edit Button */}
        {isAdmin && (
          <Suspense fallback={<Center><Loader size="sm" /></Center>}>
            <AdminEditButton menuId={menuId!} />
          </Suspense>
        )}

        {/* Menu hero image header (uses menu.imageUrl) */}
        <MenuHeroImage
          imageUrl={menu.imageUrl}
          theme={theme}
          currentLanguage={currentLanguage}
          isDarkMode={isDarkMode}
          onLanguagePickerOpen={() => setLanguageModalOpened(true)}
        />

        {/* Category Navigation */}
        <CategoryNav
          categories={filteredCategories}
          activeCategory={activeCategory}
          onCategoryChange={handleScrollSpyUpdate}
          onCategoryClick={handleCategoryClick}
          isScrollSpyActive={isScrollSpyActive}
          menuItems={allMenuItems}
          currentLanguage={currentLanguage}
          menu={menu}
          onQuestionClick={sendDirectMessage}
          drinkCategories={drinkSectionEnabled ? (menu?.drinkSection?.drinkCategories ?? []) : undefined}
          allCategories={drinkSectionEnabled ? orderedCategories : undefined}
          activeSection={drinkSectionEnabled ? activeSection : undefined}
          onSectionChange={drinkSectionEnabled ? handleSectionChange : undefined}
        />

        {/* Menu Items Section */}
        <MenuItemsSection
          categories={filteredCategories}
          itemsByCategory={filteredItemsByCategory}
          getDisplayText={getDisplayText}
          onItemSelect={handleMenuItemSelect}
          currentLanguage={currentLanguage}
        />

        {/* QR Code Section - only when restaurant has it enabled */}
        {showScanSection && (
          <QRCodeSection qrcode={menu.qrcode} theme={theme} currentLanguage={currentLanguage} />
        )}

        {/* Bottom Spacer to ensure content is visible above fixed elements */}
        <BottomSpacer theme={theme} />

        {/* Menu Chat Bar (fixed at bottom) - now includes CartButton nested inside */}
        <MenuChatBar
          newMessage={newMessage}
          setNewMessage={setNewMessage}
          handleSendMessage={handleSendMessage}
          sendDirectMessage={sendDirectMessage}
          isSending={isSending}
          messages={messages}
          menu={memoizedMenu}
          scrollToMenuItem={scrollToMenuItem}
          onMenuItemClick={handleMenuItemSelect}
          currentLanguage={currentLanguage}
          getDisplayText={getDisplayText}
          onCartButtonClick={handleCartButtonClick}
          cartEnabled={cartEnabled}
        />

        {/* Modals - cart modal only when cart is enabled for this restaurant */}
        {cartEnabled && (
          <CartModal
            opened={cartOpened}
            onClose={handleCartModalClose}
            currentLanguage={currentLanguage}
          />
        )}

        <OnboardingModal
          opened={onboardingOpened}
          onClose={handleOnboardingModalClose}
          menu={menu}
          currentLanguage={currentLanguage}
          onLanguageChange={setLanguage}
          onQuestionClick={sendDirectMessage}
        />

        {/* Language selection modal opened from hero image button */}
        <Modal
          opened={languageModalOpened}
          onClose={() => setLanguageModalOpened(false)}
          title={t('selectLanguageTitle', currentLanguage)}
          size="sm"
          centered
        >
          <Stack gap="md" mt="md">
            {['en', 'jp'].map((languageOption) => (
              <Card
                key={languageOption}
                shadow="sm"
                padding="lg"
                radius="md"
                withBorder
                style={{
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  borderColor: currentLanguage === languageOption ? (isDarkMode ? theme.colors.orange[7] : theme.colors.orange[3]) : undefined,
                  backgroundColor: currentLanguage === languageOption ? (isDarkMode ? theme.colors.orange[9] : theme.colors.orange[0]) : undefined,
                }}
                onClick={() => {
                  setLanguage(languageOption);
                  setLanguageModalOpened(false);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                <Text size="lg" fw={500} mb="xs">
                  {languageOption === 'jp' ? '日本語' : 'English'}
                </Text>
                {currentLanguage === languageOption && (
                  <Badge variant="filled" color="blue" size="sm" mt="xs">
                    {t('currentLanguageBadge', currentLanguage)}
                  </Badge>
                )}
              </Card>
            ))}
          </Stack>
        </Modal>

        {selectedMenuItem && (
          <MenuItemModal
            opened={true}
            onClose={handleMenuItemModalClose}
            item={selectedMenuItem}
            getDisplayText={getDisplayText}
            menuId={menu?.id}
            restaurantName={menu?.restaurant?.name}
            restaurantDescription={menu?.restaurant?.description}
            currentLanguage={currentLanguage}
            cartEnabled={cartEnabled}
          />
        )}
      </Container>
    </MenuLayout>
  );
};

export default Menu; 